/**
 * Page Data Loader System
 * -----------------
 *
 * This is a centralized page data loader system that handles all route data fetching for the application.
 * Instead of having multiple specialized loaders for separate pages, we've consolidated all page data fetching
 * into this single data loader that communicates with our API server.
 *
 * How it works:
 * 1. Each route uses createPageDataLoader(config, pageType) to create a loader for that route
 * 2. The pageDataLoader makes a POST request to {APIBaseURL}{pageDataEndpoint}/{pageType} with route params and query params
 *    (default endpoint: /api/v1/page_data/{pageType}, configurable via pageDataEndpoint option)
 * 3. The API server handles the request and returns data in a standardized response format
 * 4. The loader processes the response, handling errors, redirects, and authentication
 *
 * Benefits:
 * - Consistent error handling and response processing
 * - Centralized authentication flow
 * - Simplified route definitions
 * - Easier maintenance with a single loader implementation
 *
 * This approach eliminates the need for multiple loader files and consolidates all
 * data fetching logic in one place, making it easier to maintain and extend.
 *
 * Usage Example:
 * ```typescript
 * import { createPageDataLoader, createDefaultPageDataLoaderConfig } from './pageDataLoader';
 *
 * // Create a configuration (typically done once in your app setup)
 * const config = createDefaultPageDataLoaderConfig('http://localhost:3001');
 *
 * // Or create a custom configuration with your own titles/branding
 * const customConfig = {
 *   APIBaseURL: 'https://api.myapp.com',
 *   pageDataEndpoint: '/api/v1/page_data', // Custom page data endpoint (default: '/api/v1/page_data')
 *   loginURL: '/auth/login',
 *   returnToParam: 'redirect_to', // Custom query param name for login redirects
 *   timeoutMS: 15000, // Custom timeout in milliseconds (default: 10000)
 *   generateFallbackRequestID: (context) => `myapp_${context}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
 *   connectionErrorMessages: {
 *     server: 'API service unavailable. Please try again later.',
 *     client: 'Network error. Please check your connection and try again.',
 *   },
 *   errorDefaults: {
 *     notFound: {
 *       title: 'Page Not Found | MyApp',
 *       description: 'The page you are looking for could not be found.',
 *       code: 'not_found',
 *       message: 'The requested resource was not found.',
 *     },
 *     internalError: {
 *       title: 'Server Error | MyApp',
 *       description: 'An internal server error occurred.',
 *       code: 'internal_server_error',
 *       message: 'An internal server error occurred.',
 *     },
 *     authRequired: {
 *       title: 'Login Required | MyApp',
 *       description: 'You must be logged in to access this page.',
 *     },
 *     accessDenied: {
 *       title: 'Access Denied | MyApp',
 *       description: 'You do not have permission to access this page.',
 *       message: 'Sorry, you don\'t have access to this feature.',
 *       code: 'access_denied', // Standard error code (can be customized)
 *     },
 *     genericError: {
 *       title: 'Oops! | MyApp',
 *       description: 'Something unexpected happened.',
 *       message: 'Oops! Something went wrong. Please try again.',
 *       code: 'unknown_error', // Standard error code (can be customized)
 *     },
 *     invalidResponse: {
 *       title: 'Server Error | MyApp',
 *       description: 'The server sent an unexpected response.',
 *       message: 'Sorry, we received an unexpected response from the server.',
 *       code: 'invalid_response', // Standard error code (can be customized)
 *     },
 *     invalidRedirect: {
 *       title: 'Invalid Redirect | MyApp',
 *       description: 'The server attempted an invalid redirect.',
 *       message: 'Sorry, the redirect information was incomplete.',
 *       code: 'invalid_redirect', // Standard error code (can be customized)
 *     },
 *     redirectNotFollowed: {
 *       title: 'Redirect Error | MyApp',
 *       description: 'HTTP redirects from the API are not supported.',
 *       message: 'Sorry, the API attempted an unsupported redirect.',
 *       code: 'api_redirect_not_followed', // Standard error code (can be customized)
 *     },
 *     unsafeRedirect: {
 *       title: 'Unsafe Redirect Blocked | MyApp',
 *       description: 'The redirect target is not allowed for security reasons.',
 *       message: 'Sorry, this redirect is not allowed for security reasons.',
 *       code: 'unsafe_redirect', // Standard error code (can be customized)
 *     },
 *   },
 *   // Optional: Configure allowed redirect origins for security
 *   allowedRedirectOrigins: [
 *     'https://myapp.com',
 *     'https://auth.myapp.com'
 *   ],
 *   // Optional: Transform metadata when converting API errors to page errors
 *   transformErrorMeta: ({ baseMeta, originalMetadata }) => ({
 *     ...baseMeta, // Keep the base page info (title/description)
 *     // Preserve app-specific fields from the original API response
 *     account: originalMetadata?.account,
 *     site_info: originalMetadata?.site_info || { current_year: new Date().getFullYear() },
 *     user_preferences: originalMetadata?.user_preferences,
 *   }),
 *   // Optional: Custom status code handlers
 *   statusCodeHandlers: {
 *     // Handle payment required with API envelope redirect
 *     402: (statusCode, responseData, config) => {
 *       return {
 *         status: 'redirect',
 *         status_code: 200,
 *         request_id: responseData?.request_id || `redirect_${Date.now()}`,
 *         type: 'page',
 *         data: null,
 *         meta: { page: { title: 'Payment Required', description: 'Redirecting to payment page' } },
 *         error: null,
 *         redirect: { target: '/payment-required', permanent: false }
 *       };
 *     },
 *     // Custom handling for maintenance mode
 *     503: (statusCode, responseData, config) => {
 *       // Manually construct a page error response
 *       return {
 *         status: 'error',
 *         status_code: 503,
 *         request_id: responseData?.request_id || `maint_${Date.now()}`,
 *         type: 'page',
 *         data: null,
 *         meta: {
 *           page: {
 *             title: 'Maintenance Mode',
 *             description: 'Service temporarily unavailable for maintenance.'
 *           }
 *         },
 *         error: {
 *           code: 'maintenance_mode',
 *           message: 'Service temporarily unavailable for maintenance.'
 *         }
 *       };
 *     },
 *     // Override default 404 but fall back if needed
 *     404: (statusCode, responseData, config) => {
 *       if (responseData?.error?.code === 'special_not_found') {
 *         // Handle special case with manual response
 *         return {
 *           status: 'error',
 *           status_code: 404,
 *           request_id: responseData?.request_id || `404_${Date.now()}`,
 *           type: 'page',
 *           data: null,
 *           meta: { page: { title: 'Special Not Found', description: 'Special page not found' } },
 *           error: { code: 'special_not_found', message: 'Special page not found' }
 *         };
 *       }
 *       // Fall back to default handling
 *       return null;
 *     }
 *   }
 * };
 *
 * // Create loaders for each page type
 * const homeLoader = createPageDataLoader(config, 'home');
 * const dashboardLoader = createPageDataLoader(config, 'dashboard');
 * const profileLoader = createPageDataLoader(config, 'profile');
 *
 * // Use in React Router
 * export const routes: RouteObject[] = [
 *   { path: '/', loader: homeLoader, element: <HomePage /> },
 *   { path: '/dashboard', loader: dashboardLoader, element: <DashboardPage /> },
 *   { path: '/profile/:id', loader: profileLoader, element: <ProfilePage /> },
 * ];
 * ```
 */

import type { LoaderFunctionArgs } from 'react-router';
import qs from 'qs';
import type {
  PageResponseEnvelope,
  BaseMeta,
  APIResponseEnvelope,
} from '../api-envelope/api-envelope-types';
import type { SSRHelpers } from '../types';
import { getDevMode } from 'lifecycleion/dev-mode';
import {
  createBaseHeaders,
  createErrorResponse,
  decorateWithSsrOnlyData,
  fetchWithTimeout,
} from './page-data-loader-utils';
import type {
  PageDataLoaderConfig,
  PageDataLoaderOptions,
  LocalPageHandler,
  LocalPageHandlerParams,
  LocalPageDataLoaderConfig,
  LocalPageDataLoaderConfigOverrides,
  ErrorDefaults,
  PageDataLoaderConfigOverrides,
} from './page-data-loader-types';
import { APIResponseHelpers } from '../api-envelope/response-helpers';
import {
  processRedirectResponse,
  processAPIResponse,
} from './page-data-loader-helpers';
import {
  DEBUG_PAGE_LOADER,
  DEFAULT_CONNECTION_ERROR_MESSAGES,
  DEFAULT_ERROR_DEFAULTS,
  DEFAULT_LOGIN_URL,
  DEFAULT_RETURN_TO_PARAM,
  DEFAULT_PAGE_DATA_ENDPOINT,
  DEFAULT_FALLBACK_REQUEST_ID_GENERATOR,
  DEFAULT_TIMEOUT_MS,
} from './page-data-loader-consts';

/**
 * Creates a default configuration object with sensible defaults
 */
export function createDefaultPageDataLoaderConfig(
  APIBaseURL: string,
  overrides: PageDataLoaderConfigOverrides = {},
): PageDataLoaderConfig {
  // Overrides are applied shallowly. If you override nested objects such as
  // errorDefaults or connectionErrorMessages, provide the full nested object
  // shape you want to use.
  const {
    pageDataEndpoint = DEFAULT_PAGE_DATA_ENDPOINT,
    loginURL = DEFAULT_LOGIN_URL,
    returnToParam = DEFAULT_RETURN_TO_PARAM,
    statusCodeHandlers,
    ...localOverrides
  } = overrides;

  const defaultLocalConfig =
    createDefaultLocalPageDataLoaderConfig(localOverrides);

  const config: PageDataLoaderConfig = {
    APIBaseURL,
    ...defaultLocalConfig,
  };

  config.pageDataEndpoint = pageDataEndpoint;
  config.loginURL = loginURL;
  config.returnToParam = returnToParam;

  if (statusCodeHandlers) {
    config.statusCodeHandlers = statusCodeHandlers;
  }

  return config;
}

export function createDefaultLocalPageDataLoaderConfig(
  overrides: LocalPageDataLoaderConfigOverrides = {},
): LocalPageDataLoaderConfig {
  // Overrides are applied shallowly. If you override nested objects such as
  // errorDefaults or connectionErrorMessages, provide the full nested object
  // shape you want to use.
  // Deep-clone nested defaults to avoid shared references
  const errorDefaultsClone: ErrorDefaults = JSON.parse(
    JSON.stringify(DEFAULT_ERROR_DEFAULTS),
  ) as ErrorDefaults;

  const connectionErrorMessagesClone = {
    ...DEFAULT_CONNECTION_ERROR_MESSAGES,
  } as const;

  return {
    errorDefaults: errorDefaultsClone,
    connectionErrorMessages: connectionErrorMessagesClone,
    loginURL: DEFAULT_LOGIN_URL,
    returnToParam: DEFAULT_RETURN_TO_PARAM,
    generateFallbackRequestID: DEFAULT_FALLBACK_REQUEST_ID_GENERATOR,
    timeoutMS: DEFAULT_TIMEOUT_MS,
    ...overrides,
  };
}

// Define a factory function to create page data loaders with specific page types
export function createPageDataLoader(
  config: PageDataLoaderConfig,
  pageType: string,
): (args: LoaderFunctionArgs) => Promise<unknown>;
export function createPageDataLoader(
  config: LocalPageDataLoaderConfig,
  handler: LocalPageHandler,
): (args: LoaderFunctionArgs) => Promise<unknown>;
export function createPageDataLoader(
  config: PageDataLoaderConfig | LocalPageDataLoaderConfig,
  pageTypeOrHandler: string | LocalPageHandler,
) {
  // If the pageTypeOrHandler is a string, create a page data loader that uses the page type
  if (typeof pageTypeOrHandler === 'string') {
    const pageType = pageTypeOrHandler;

    return ({ request, params }: LoaderFunctionArgs) =>
      pageDataLoader({
        request,
        params,
        pageType,
        config: config as PageDataLoaderConfig,
      });
  }

  // If the pageTypeOrHandler is a LocalPageHandler, create a page data loader that uses the handler
  const handler = pageTypeOrHandler;
  return (args: LoaderFunctionArgs) =>
    localPageDataLoader(config as LocalPageDataLoaderConfig, handler, args);
}

/**
 * Main page data loader function that handles data fetching for a specific page type
 */
async function pageDataLoader({
  request,
  params,
  pageType,
  config,
}: PageDataLoaderOptions): Promise<PageResponseEnvelope> {
  const isServer = typeof window === 'undefined';
  const SSRHelpers = (request as unknown as { SSRHelpers?: SSRHelpers })
    .SSRHelpers;

  // Per-request isDevelopment: prefer the Fastify request decoration (set by
  // the onRequest hook), fall back to the global for client-side loaders.
  const isDevelopment =
    (isServer
      ? (SSRHelpers?.fastifyRequest as { isDevelopment?: boolean } | undefined)
          ?.isDevelopment
      : undefined) ?? getDevMode();

  // Get the API server URL (already normalized)
  const APIBaseURL = config.APIBaseURL;
  const pageDataEndpoint =
    config.pageDataEndpoint || DEFAULT_PAGE_DATA_ENDPOINT;

  // Single source of truth for the external page-data URL
  // Note: Internal short-circuit calls do NOT rely on this URL; they reuse the
  // same routing context we place in requestBody (route_params, query_params,
  // request_path, original_url) to ensure consistency.
  const apiEndpoint = `${APIBaseURL}${pageDataEndpoint}/${pageType}`;

  // build the request body
  const url = new URL(request.url);

  // Convert params to ensure all values are strings
  const routeParams: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    routeParams[key] = value || '';
  }

  // Assemble the request body
  const requestBody: Record<string, unknown> = {
    route_params: routeParams, // react router params
    query_params: qs.parse(url.search, { ignoreQueryPrefix: true }),
    // Include the requested path and original URL for debugging
    request_path: url.pathname,
    original_url: request.url,
  };

  // Forward SSR request context to API server (SSR-only, not sent from browser)
  if (isServer && SSRHelpers?.fastifyRequest) {
    const fastifyReq = SSRHelpers.fastifyRequest as {
      requestContext?: Record<string, unknown>;
    };

    // Only include if there's actual data (skip empty objects and arrays)
    if (
      fastifyReq.requestContext &&
      !Array.isArray(fastifyReq.requestContext) &&
      Object.keys(fastifyReq.requestContext).length > 0
    ) {
      requestBody.ssr_request_context = fastifyReq.requestContext;
    }
  }

  try {
    if (isServer) {
      if (DEBUG_PAGE_LOADER) {
        const hasInternalHandler = !!SSRHelpers?.handlers?.hasHandler(pageType);

        // eslint-disable-next-line no-console
        console.log('[pageDataLoader] server-side data fetching decision', {
          pageType,
          SSRHelpersAttached: !!SSRHelpers,
          hasInternalHandler,
          strategy: hasInternalHandler
            ? 'internal_short_circuit'
            : 'http_fetch',
        });
      }

      // If SSRHelper is available (not undefined) and there is a registered handler, try internal call first before falling back to HTTP fetch
      if (SSRHelpers?.handlers?.hasHandler(pageType)) {
        // Note: internal_short_circuit selected

        try {
          const outcome = await SSRHelpers.handlers.callHandler({
            originalRequest: SSRHelpers.fastifyRequest,
            controlledReply: SSRHelpers.controlledReply,
            pageType,
            timeoutMS: config.timeoutMS ?? DEFAULT_TIMEOUT_MS,
            // Pass the exact same data that would be in the POST body (converted from snake_case)
            // Extract from requestBody (React Router context from frontend loader)
            // Fallback to empty values if requestBody is malformed (defensive programming)
            // Note: These represent the React Router URL/params, NOT the Fastify request URL
            routeParams:
              (requestBody.route_params as Record<string, string>) || {},
            queryParams:
              (requestBody.query_params as Record<string, unknown>) || {},
            requestPath: (requestBody.request_path as string) || '',
            originalURL: (requestBody.original_url as string) || '',
          });

          if (outcome.exists && outcome.result) {
            // Internal handler returned an envelope
            const result = outcome.result as PageResponseEnvelope;

            if (result.type === 'page') {
              if (
                result.status === 'redirect' &&
                result.type === 'page' &&
                result.redirect
              ) {
                return processRedirectResponse(
                  config,
                  result as unknown as Record<string, unknown>,
                  {},
                );
              }

              return decorateWithSsrOnlyData(result, {});
            }
          } else if (DEBUG_PAGE_LOADER) {
            // No internal handler; fall back to HTTP fetch
            // eslint-disable-next-line no-console
            console.warn(
              `[pageDataLoader] fallback to http_fetch for ${pageType}`,
            );
          }
        } catch (internalError) {
          if (DEBUG_PAGE_LOADER) {
            // eslint-disable-next-line no-console
            console.error(
              `[pageDataLoader] Internal handler error for ${pageType}; converting to 500 error`,
              internalError,
            );
          }

          // Choose a clearer message for internal handler timeouts vs. generic internal errors
          const isHandlerTimeout =
            internalError instanceof Error &&
            (internalError as unknown as { errorCode?: string }).errorCode ===
              'handler_timeout';

          // Safe, user-facing messages; do not expose internal error messages
          // For internal timeouts, reuse the server connection error message if provided
          const message = isHandlerTimeout
            ? config.connectionErrorMessages?.server ||
              DEFAULT_CONNECTION_ERROR_MESSAGES.server
            : config.errorDefaults.internalError.message;

          // Return a 500 error envelope immediately to avoid reattempt via HTTP
          return decorateWithSsrOnlyData(
            createErrorResponse(
              config,
              500,
              config.errorDefaults.internalError.code,
              message,
              undefined,
              undefined,
              isDevelopment
                ? internalError instanceof Error
                  ? {
                      name: internalError.name,
                      message: internalError.message,
                      stack: internalError.stack,
                      ...(isHandlerTimeout
                        ? {
                            errorCode: 'handler_timeout',
                            timeoutMS: (
                              internalError as unknown as { timeoutMS?: number }
                            ).timeoutMS,
                          }
                        : {}),
                    }
                  : { value: String(internalError) }
                : undefined,
            ),
            {},
          );
        }
      }

      // Set to JSON and copy relevant headers from ssr request to api server
      // Forward: cookies, language preferences, and tracking headers

      const headers = createBaseHeaders();

      // Properly access headers from the Request object
      const xssrRequest = request.headers.get('x-ssr-request');
      const originalIP = request.headers.get('x-ssr-original-ip');
      const userAgent = request.headers.get('user-agent');
      const correlationID = request.headers.get('x-correlation-id');
      const cookie = request.headers.get('cookie');
      const acceptLanguage = request.headers.get('accept-language');

      // Set headers if they exist
      if (xssrRequest) {
        headers.set('X-SSR-Request', xssrRequest);
      }

      if (originalIP) {
        headers.set('X-SSR-Original-IP', originalIP);
      }

      if (userAgent) {
        headers.set('X-SSR-Forwarded-User-Agent', userAgent);
      }

      if (correlationID) {
        headers.set('X-Correlation-ID', correlationID);
      }

      if (cookie) {
        headers.set('Cookie', cookie);
      }

      // Forward Accept-Language header for internationalization support
      if (acceptLanguage) {
        headers.set('Accept-Language', acceptLanguage);
      }

      const response = await fetchWithTimeout(
        apiEndpoint,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          redirect: 'manual', // Don't automatically follow redirects
        },
        config.timeoutMS ?? DEFAULT_TIMEOUT_MS,
      );

      const result = await processAPIResponse(response, config, isDevelopment);

      // Merge ssr_request_context from API response back into SSR request (SSR-only)
      if (
        result.ssr_request_context &&
        typeof result.ssr_request_context === 'object' &&
        !Array.isArray(result.ssr_request_context) &&
        Object.keys(result.ssr_request_context).length > 0 &&
        SSRHelpers?.fastifyRequest
      ) {
        const fastifyReq = SSRHelpers.fastifyRequest as {
          requestContext?: Record<string, unknown>;
        };

        if (!fastifyReq.requestContext) {
          fastifyReq.requestContext = {};
        }

        Object.assign(fastifyReq.requestContext, result.ssr_request_context);
      }

      return result;
    } else {
      if (DEBUG_PAGE_LOADER) {
        // eslint-disable-next-line no-console
        console.log(`Client side data fetching for ${pageType} page`);
      }

      // Client side data fetching
      const headers = createBaseHeaders();

      // Forward Accept-Language header for internationalization support
      // In the browser, navigator.languages provides the user's preferred languages
      if (typeof navigator !== 'undefined') {
        if (navigator.languages && navigator.languages.length) {
          headers.set('Accept-Language', navigator.languages.join(','));
        } else if (navigator.language) {
          headers.set('Accept-Language', navigator.language);
        }
      }

      // On client side, we include credentials: 'include' to allow cookies to be sent
      const response = await fetchWithTimeout(
        apiEndpoint,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          credentials: 'include', // This allows cookies to be sent with the request
          redirect: 'manual', // Don't automatically follow redirects
        },
        config.timeoutMS ?? DEFAULT_TIMEOUT_MS,
      );

      return processAPIResponse(response, config, isDevelopment);
    }
  } catch (error) {
    if (DEBUG_PAGE_LOADER) {
      // eslint-disable-next-line no-console
      console.error('Error fetching page data:', error);
    }

    // Check for common connection errors
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to fetch data from server';

    const isConnectionError =
      errorMessage.includes('fetch failed') ||
      errorMessage.includes('Unable to connect') ||
      errorMessage.includes('ECONNREFUSED') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Request timeout after'); // Treat timeout as connection error

    // Create appropriate message based on server/client context
    let friendlyMessage = errorMessage;

    if (isConnectionError) {
      friendlyMessage = isServer
        ? config.connectionErrorMessages?.server ||
          DEFAULT_CONNECTION_ERROR_MESSAGES.server
        : config.connectionErrorMessages?.client ||
          DEFAULT_CONNECTION_ERROR_MESSAGES.client;
    }

    // Network or other errors that prevent the fetch from completing
    // No cookies would be available here since the fetch failed
    return decorateWithSsrOnlyData(
      createErrorResponse(
        config,
        500,
        config.errorDefaults.internalError.code,
        friendlyMessage,
      ),
      {}, // No SSR-only data for failed fetches
    );
  }
}

/**
 * Local page data loader (framework does not perform HTTP or short-circuit calls)
 *
 * Purpose:
 * - Run a page data loader locally without the framework doing an HTTP fetch or short-circuit calls
 * - Preserve the same ergonomics as the normal loader: timeout handling,
 *   redirect support, envelope validation, and consistent error envelopes
 *
 * Notes:
 * - Your handler may still perform its own fetch/database calls; the timeout
 *   here applies to the entire handler execution
 * - The handler receives `LocalPageHandlerParams` (no Fastify request object)
 * - `invocationOrigin` is set to "local" for debugging
 * - Timeout uses `config.timeoutMS` (0 disables); local timeout failures reuse
 *   `connectionErrorMessages.server` for parity with the HTTP loader path
 */
async function localPageDataLoader<T = unknown, M extends BaseMeta = BaseMeta>(
  config: LocalPageDataLoaderConfig,
  handler: LocalPageHandler<T, M>,
  { request, params }: LoaderFunctionArgs,
): Promise<unknown> {
  const url = new URL(request.url);

  // Convert params to ensure all values are strings
  const routeParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    routeParams[key] = value || '';
  }

  // Assemble the local handler params with origin and routing context
  const localParams: LocalPageHandlerParams = {
    pageType: 'local',
    invocationOrigin: 'local',
    routeParams: routeParams,
    queryParams: qs.parse(url.search, { ignoreQueryPrefix: true }),
    requestPath: url.pathname,
    originalURL: request.url,
  };

  // Configure timeout (0 disables)
  const timeoutMS = config.timeoutMS ?? DEFAULT_TIMEOUT_MS;

  // Defer invocation to the microtask queue and normalize to a Promise.
  // Using Promise.resolve().then(() => ...) ensures synchronous throws from
  // the handler become Promise rejections instead of escaping before our
  // timeout race is set up. Non-Promise returns are treated as resolved values.
  const invocation = Promise.resolve().then(() => handler(localParams));

  // Attach a no-op catch when using a timeout to prevent a possible
  // unhandledRejection if the timeout "wins" and the handler later rejects.
  if (timeoutMS && timeoutMS > 0) {
    void invocation.catch(() => {});
  }

  // Track the timeout ID to ensure it is cleared regardless of timeout path
  let timeoutID: ReturnType<typeof setTimeout> | undefined;

  // Build a single promise that either resolves to the handler result or rejects on timeout
  const resultPromise: Promise<PageResponseEnvelope | APIResponseEnvelope> =
    // Check if a timeout is specified
    !timeoutMS || timeoutMS <= 0
      ? // No timeout specified, return the handler result immediately
        // Handler promise when no timer is specified
        (invocation as Promise<PageResponseEnvelope | APIResponseEnvelope>)
      : // If a timeout is specified, race the handler promise with a timer promise
        (Promise.race([
          // Handler promise
          invocation,
          // Timer promise
          new Promise<never>((_, reject) => {
            timeoutID = setTimeout(() => {
              const error = new Error(`Request timeout after ${timeoutMS}ms`);
              (error as unknown as { errorCode: string }).errorCode =
                'handler_timeout';
              (error as unknown as { timeoutMS: number }).timeoutMS = timeoutMS;
              reject(error);
            }, timeoutMS);
          }),
        ]) as Promise<PageResponseEnvelope | APIResponseEnvelope>);

  try {
    // Ensure timer cleared regardless of outcome
    const result = await resultPromise.finally(() => {
      if (timeoutID) {
        clearTimeout(timeoutID);
      }
    });

    // Validate that the handler returned a proper envelope object
    if (!APIResponseHelpers.isValidEnvelope(result)) {
      return createErrorResponse(
        config,
        500,
        config.errorDefaults.invalidResponse.code,
        config.errorDefaults.invalidResponse.message,
      );
    }

    // Handle API-style redirect envelopes (status: "redirect")
    if (
      result.status === 'redirect' &&
      result.type === 'page' &&
      result.redirect
    ) {
      return processRedirectResponse(
        config,
        result as unknown as Record<string, unknown>,
        {},
      );
    }

    // Success or error envelopes are returned as-is and decorated
    // Note:
    // - Status codes provided by the handler's envelope (status_code) are preserved.
    //   The SSR base renderer will read loaderData and set the HTTP status accordingly.
    // - SSR-only cookies are NOT available in the local path because there is no HTTP
    //   response to extract Set-Cookie headers from. If you need to set cookies, use
    //   the HTTP-backed loader path (API fetch) so cookies can be forwarded via __ssOnly.
    return decorateWithSsrOnlyData(result as PageResponseEnvelope, {});
  } catch (internalError) {
    const isDevelopment = getDevMode();

    // Identify timeout errors produced by the race above
    const isHandlerTimeout =
      internalError instanceof Error &&
      (internalError as unknown as { errorCode?: string }).errorCode ===
        'handler_timeout';

    // Local timeout failures reuse the same user-facing message bucket as
    // HTTP timeout/connection-style failures.
    const message = isHandlerTimeout
      ? config.connectionErrorMessages?.server ||
        DEFAULT_CONNECTION_ERROR_MESSAGES.server
      : config.errorDefaults.internalError.message;

    // Build a standardized page error envelope
    return decorateWithSsrOnlyData(
      createErrorResponse(
        config,
        500,
        config.errorDefaults.internalError.code,
        message,
        undefined,
        undefined,
        isDevelopment
          ? internalError instanceof Error
            ? {
                name: internalError.name,
                message: internalError.message,
                stack: internalError.stack,
                ...(isHandlerTimeout
                  ? {
                      errorCode: 'handler_timeout',
                      timeoutMS: (
                        internalError as unknown as { timeoutMS?: number }
                      ).timeoutMS,
                    }
                  : {}),
              }
            : { value: String(internalError) }
          : undefined,
      ),
      {},
    );
  }
}
